import { Button } from "@itandi/itandi-bb-ui/Button";
import { Form } from "@itandi/itandi-bb-ui/Form";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import type { ComponentProps } from "react";
import { useCallback, useId, useState } from "react";
import useSWRMutation from "swr/mutation";
import { dealCacheKey } from "~/hooks/vendors/useDeals";
import { useEnabledVendorUsers } from "~/hooks/vendors/useVendorUsers";
import type { DealVendorCompany } from "~/models/DealVendorCompany";
import type { VendorUser } from "~/models/VendorUser";
import { isSuccess } from "~/utils/api/api_handler";
import { update } from "~/utils/api/vendors/deals/DealVendorCompany";

type Props = {
	dealVendorCompany: DealVendorCompany;
	handleClose: () => void;
};

/**
 * Vendorが自分のDealVendorCompanyの情報をupdateするためのModal
 *
 * @param handleClose
 * @param dealVendorCompany
 * @constructor
 * @for vendor
 */
export function DealVendorChangeModal({
	handleClose,
	dealVendorCompany
}: Props) {
	const { notify } = useNotification();
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const formId = useId();
	const { data: vendorUsers, isLoading: isLoadingVendorUsers } =
		useEnabledVendorUsers();
	const [userId, setUserId] = useState<number | null>(
		dealVendorCompany.user?.id ?? null
	);

	const { trigger, isMutating } = useSWRMutation(dealCacheKey(id), async () => {
		return await update(id, dealVendorCompany, userId, null);
	});

	const onSubmit = useCallback(async () => {
		const result = await trigger();
		if (isSuccess(result)) {
			handleClose();
			notify({ content: "更新しました。", variant: "success" });
		} else {
			notify({
				content: `エラーが発生しました。${result.message ?? ""}`,
				variant: "error"
			});
		}
	}, [trigger, handleClose, notify]);

	const selectedVendorUser =
		(vendorUsers ?? []).find((user) => user.id === userId) ??
		(dealVendorCompany.user?.id === userId ? dealVendorCompany.user : null);

	const selectedUserOption = selectedVendorUser
		? { label: selectedVendorUser.name, value: String(selectedVendorUser.id) }
		: null;

	const onChangeUser: ComponentProps<typeof SingleComboBox>["onChange"] = (
		option
	): void => {
		setUserId(option?.value ? Number(option.value) : null);
	};

	return (
		<Modalv2 isOpen onClose={handleClose} size="s">
			<ModalHeader title="担当者の変更" />
			<ModalBody>
				<Form id={formId} onSubmit={onSubmit}>
					<FormGroup variant="vertical">
						<FormControl chip="必須" label="担当者">
							<SingleComboBox
								loading={isLoadingVendorUsers}
								onChange={onChangeUser}
								options={(vendorUsers ?? []).map((user: VendorUser) => ({
									label: user.name,
									value: String(user.id)
								}))}
								placeholder="選択してください"
								value={selectedUserOption}
							/>
						</FormControl>
					</FormGroup>
				</Form>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						disabled={isMutating}
						onClick={handleClose}
						variant="secondary"
					>
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						disabled={userId === null}
						form={formId}
						loading={isMutating}
						type="submit"
						variant="primary"
					>
						変更
					</Button>
				}
			/>
		</Modalv2>
	);
}
